import { snapToBeat } from 'fym-shared/src/music24/musicVideoDoc';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';

/**
 * The pure maths of the timeline (design music-video Timeline, c5): the visible part of the song,
 * seconds to percent and back, the ruler, the beat ticks, the snap. The lanes place every block in
 * percent of the lane width, so a resize needs no new maths.
 */

/** The visible part of the song, in seconds. */
export interface TimelineView {
  from: number;
  to: number;
}

/**
 * "Whole song" or "Section" (design Timeline: the zoom of the bar), or `custom`: a part that the
 * user zoomed to (the + and − buttons, Ctrl/⌘ and the wheel, a pinch) and scrolls.
 */
export type TimelineZoom = 'song' | 'section' | 'custom';

/** The section colours of the design (`mv.css` `.s-intro` … `.s-outro`). */
export type SectionTone = 'intro' | 'verse' | 'chorus' | 'bridge' | 'outro';

/** The visible part: the whole video, or the selected section (else the whole video). */
export function timelineView(
  zoom: TimelineZoom,
  range: { startSec: number; endSec: number },
  section: Pick<MusicVideoSongMapSection, 'startSec' | 'endSec'> | null
): TimelineView {
  if (zoom === 'section' && section !== null && section.endSec > section.startSec) {
    return { from: section.startSec, to: section.endSec };
  }
  return { from: range.startSec, to: Math.max(range.endSec, range.startSec + 1) };
}

/** The shortest part of the song that the zoom shows. */
export const MIN_VIEW_SEC = 4;

/** One click of the + or − button zooms by this factor. */
export const ZOOM_STEP = 1.6;

/** A part of `spanSec` from `from`, inside the video. The span is at least `MIN_VIEW_SEC`. Pure. */
export function clampView(from: number, spanSec: number, range: { startSec: number; endSec: number }): TimelineView {
  const whole = Math.max(range.endSec - range.startSec, 1);
  const span = Math.min(whole, Math.max(Math.min(MIN_VIEW_SEC, whole), spanSec));
  const start = Math.min(range.endSec - span, Math.max(range.startSec, from));
  return { from: Math.max(range.startSec, start), to: Math.max(range.startSec, start) + span };
}

/**
 * Zooms the view by `factor` (below 1 zooms in) and keeps `anchorSec` at the same place on the
 * lane (the pointer of the wheel, or the play head). Pure.
 */
export function zoomViewAt(
  view: TimelineView,
  range: { startSec: number; endSec: number },
  factor: number,
  anchorSec: number
): TimelineView {
  const span = view.to - view.from;
  const share = span > 0 ? Math.min(1, Math.max(0, (anchorSec - view.from) / span)) : 0.5;
  const nextSpan = span * factor;
  const clamped = clampView(0, nextSpan, range);
  const nextSpanClamped = clamped.to - clamped.from;
  return clampView(anchorSec - share * nextSpanClamped, nextSpanClamped, range);
}

/** Moves the view by `deltaSec` (the scroll of the wheel, the scroll bar). Pure. */
export function panView(
  view: TimelineView,
  range: { startSec: number; endSec: number },
  deltaSec: number
): TimelineView {
  return clampView(view.from + deltaSec, view.to - view.from, range);
}

/** True when the view shows the whole video. */
export function isWholeView(view: TimelineView, range: { startSec: number; endSec: number }): boolean {
  return view.from <= range.startSec + 0.001 && view.to >= range.endSec - 0.001;
}

/**
 * The view while the song plays: when the play head leaves the view, the next page starts a little
 * before it, so the play head stays on screen. Pure.
 */
export function followPlayHead(
  view: TimelineView,
  range: { startSec: number; endSec: number },
  sec: number
): TimelineView {
  const span = view.to - view.from;
  if (sec >= view.from && sec <= view.to - span * 0.02) {
    return view;
  }
  return clampView(sec - span * 0.1, span, range);
}

/**
 * A cut between two clips gets a drag handle only when both clips are at least this wide on screen.
 * A narrower handle would cover the clips, and a click would move the cut in place of selecting the
 * clip (the whole-song view of a 4-minute cut has clips of 3 px).
 */
export const MIN_EDGE_CLIP_PX = 16;

/** True when the cut between `left` and `right` gets a drag handle at this zoom. Pure. */
export function showsEdge(
  left: { startSec: number; endSec: number },
  right: { startSec: number; endSec: number },
  pxPerSec: number
): boolean {
  return (
    (left.endSec - left.startSec) * pxPerSec >= MIN_EDGE_CLIP_PX &&
    (right.endSec - right.startSec) * pxPerSec >= MIN_EDGE_CLIP_PX
  );
}

/** The place of `sec` in percent of the lane (can be outside 0..100). */
export function secToPercent(sec: number, view: TimelineView): number {
  return ((sec - view.from) / (view.to - view.from)) * 100;
}

/** The second under a share of the lane (0..1), clamped to the view. */
export function shareToSec(share: number, view: TimelineView): number {
  const clamped = Math.min(1, Math.max(0, share));
  return view.from + clamped * (view.to - view.from);
}

/** The second under a pointer x on a lane element. */
export function pointerToSec(clientX: number, rect: { left: number; width: number }, view: TimelineView): number {
  return rect.width <= 0 ? view.from : shareToSec((clientX - rect.left) / rect.width, view);
}

/** The left and the width of a span in percent, or null when it is outside the view. */
export function spanBox(startSec: number, endSec: number, view: TimelineView): { left: number; width: number } | null {
  if (endSec <= view.from || startSec >= view.to) {
    return null;
  }
  const left = secToPercent(Math.max(startSec, view.from), view);
  const right = secToPercent(Math.min(endSec, view.to), view);
  return { left, width: Math.max(0, right - left) };
}

/** `m:ss`, or `m:ss.t` with tenths ("0:51.2", design b1). */
export function formatTime(sec: number, withTenths = false): string {
  const safe = Math.max(0, sec);
  const minutes = Math.floor(safe / 60);
  const rest = safe - minutes * 60;
  if (withTenths) {
    const tenths = Math.floor(rest * 10) / 10;
    const whole = Math.floor(tenths);
    return `${minutes}:${String(whole).padStart(2, '0')}.${Math.round((tenths - whole) * 10)}`;
  }
  return `${minutes}:${String(Math.floor(rest)).padStart(2, '0')}`;
}

/** The ruler marks: every 30 s on a long view, 5 s on a section, 2 s on a short part (design Timeline). */
export function rulerTicks(view: TimelineView): Array<{ sec: number; label: string }> {
  const span = view.to - view.from;
  const step = span > 120 ? 30 : span > 40 ? 5 : 2;
  const ticks: Array<{ sec: number; label: string }> = [];
  for (let sec = Math.ceil(view.from / step) * step; sec <= view.to; sec += step) {
    ticks.push({ sec, label: formatTime(sec) });
  }
  return ticks;
}

/**
 * The beat ticks of the song lane inside the view. A long view shows the bar lines (downbeats)
 * only, so the ticks stay readable (design Timeline: "the waveform with the bar lines").
 */
export function beatTicks(beats: readonly number[], downbeats: readonly number[], view: TimelineView): number[] {
  const span = view.to - view.from;
  const source = span > 120 && downbeats.length > 0 ? downbeats : beats;
  return source.filter((sec) => sec >= view.from && sec <= view.to);
}

/** The colour of a section kind. An unknown kind gets the verse colour. */
export function sectionTone(kind: string): SectionTone {
  const lower = kind.toLowerCase();
  if (lower.includes('intro')) {
    return 'intro';
  }
  if (lower.includes('outro') || lower.includes('end')) {
    return 'outro';
  }
  if (lower.includes('bridge') || lower.includes('break')) {
    return 'bridge';
  }
  if ((lower.includes('chorus') && !lower.includes('pre')) || lower.includes('drop') || lower.includes('hook')) {
    return 'chorus';
  }
  return 'verse';
}

/** The length of one beat of the song in seconds; 0.5 s (120 bpm) when the tempo is unknown. */
export function beatSec(bpm: number): number {
  return bpm > 0 ? 60 / bpm : 0.5;
}

/**
 * The place of a drag or a drop: the nearest beat (design c5: "Snaps to the beat"), or the pointer
 * itself while Alt is down or when snap is off.
 */
export function snapTime(sec: number, beats: readonly number[], bpm: number, free: boolean): number {
  if (free) {
    return sec;
  }
  return snapToBeat(sec, beats, beatSec(bpm) / 2 + 0.001);
}

/** "bar 27, beat 1" of a second (design c5 drag tag). Null before the first downbeat. */
export function barAndBeat(
  sec: number,
  beats: readonly number[],
  downbeats: readonly number[]
): { bar: number; beat: number } | null {
  const barIndex = downbeats.filter((time) => time <= sec + 0.001).length;
  const barStart = downbeats[barIndex - 1];
  if (barStart === undefined) {
    return null;
  }
  const beat = beats.filter((time) => time >= barStart - 0.001 && time <= sec + 0.001).length;
  return { bar: barIndex, beat: Math.max(1, beat) };
}

/** The section that holds `sec`, or null. */
export function sectionAt<T extends Pick<MusicVideoSongMapSection, 'startSec' | 'endSec'>>(
  sections: readonly T[],
  sec: number
): T | null {
  return sections.find((section) => sec >= section.startSec && sec < section.endSec) ?? null;
}

/**
 * The beat where "Split at beat" cuts a clip (design b1): the beat nearest the play head when the
 * play head is inside the clip, else the beat nearest the middle of the clip. Null when no beat
 * leaves both parts at least `minSec` long.
 */
export function splitPoint(
  clip: { startSec: number; endSec: number },
  beats: readonly number[],
  playSec: number,
  minSec: number
): number | null {
  const inside = beats.filter((sec) => sec >= clip.startSec + minSec && sec <= clip.endSec - minSec);
  if (inside.length === 0) {
    return null;
  }
  const target = playSec > clip.startSec && playSec < clip.endSec ? playSec : (clip.startSec + clip.endSec) / 2;
  return inside.reduce((best, sec) => (Math.abs(sec - target) < Math.abs(best - target) ? sec : best));
}

/** The time of `beats` beats from `sec` (the default length of a dropped clip). */
export function beatsLengthSec(beats: number, bpm: number): number {
  return beats * beatSec(bpm);
}

/** A dropped clip takes this many beats, or less when the footage is shorter (design c5). */
export const DROP_CLIP_BEATS = 4;

/** The length of a dropped clip: `DROP_CLIP_BEATS` beats, at most the length of the footage. Pure. */
export function dropLengthSec(bpm: number, footageSec: number | null): number {
  const beats = beatsLengthSec(DROP_CLIP_BEATS, bpm);
  return footageSec === null || footageSec <= 0 ? beats : Math.min(beats, footageSec);
}
